import React, { useState } from 'react';
import { Button, Spinner } from '@amzn/awsui-components-react';
import ErrorUtils from 'src/utils/ErrorUtils';
import { SegmentExpansionStatus } from 'src/components/reports/lefReport/useSegmentCliExpansion';
import { MAX_SEGMENT_PAGES, SEGMENT_FETCH_PAGE_SIZE } from 'src/components/reports/cliProvenance';
import {
    CALC_BUILDER_LOOKUP_FAILED_MESSAGE,
    CALC_BUILDER_NO_ACCESS_MESSAGE,
} from 'src/components/reports/lefReport/calcBuilderLink';
import { WIZARD_STEP_NUMBER } from 'src/components/calculation-builder/wizardStepDeepLink';
import 'src/components/reports/lefReport/CliProvenanceHeaders.scss';
import { CliSourcesState } from 'src/components/reports/lefReport/useCliSources';

const pluralCli = (count: number) => `${count} CLI${count === 1 ? '' : 's'}`;

/** Caption under a segment-total header: what a click will do, or what is happening. */
export function segmentTotalCaption(status: SegmentExpansionStatus): string {
    if (status.loading) {
        return 'Loading CLIs…';
    }
    if (status.error) {
        return 'Could not load CLIs. Select to retry.';
    }
    if (status.expanded && status.cliCount !== undefined) {
        const shown = `${pluralCli(status.cliCount)} shown`;
        return status.truncated ? `${shown} (first ${MAX_SEGMENT_PAGES * (SEGMENT_FETCH_PAGE_SIZE - 1)} only)` : shown;
    }
    if (status.cliCount !== undefined) {
        return pluralCli(status.cliCount);
    }
    return 'Show CLIs';
}

/**
 * "Total <functional segment>" column header (Story 3.1). A real button, so it is keyboard- and
 * screen-reader-operable; the dotted underline is the drillable affordance that tells it apart from
 * the non-drillable reconciliation headers.
 */
export function SegmentTotalHeader(props: {
    label: string;
    segmentName: string;
    status: SegmentExpansionStatus;
    onToggle: () => void;
}) {
    const { label, segmentName, status, onToggle } = props;
    const action = status.expanded ? 'Hide' : 'Show';
    return (
        <div className="lef-cli-provenance__segment-header">
            <button
                type="button"
                className="lef-cli-provenance__segment-toggle"
                aria-expanded={status.expanded}
                aria-label={`${action} CLIs in ${segmentName}`}
                title={`${action} the CLIs in ${segmentName}`}
                onClick={onToggle}>
                {label}
            </button>
            <span
                className={`lef-cli-provenance__caption${status.error ? ' lef-cli-provenance__caption--error' : ''}`}
                role={status.error ? 'alert' : undefined}>
                {status.loading && <Spinner size="normal" />} {segmentTotalCaption(status)}
            </span>
            {status.error && <span className="lef-cli-provenance__visually-hidden">{status.error}</span>}
        </div>
    );
}

export interface CalcBuilderLinkProps {
    /** False when the user's role cannot view Calculation Builder (Story 3.5 AC5). */
    canOpen: boolean;
    /**
     * Looks up the CLI and opens its Calc Builder page in a new tab; rejects on failure. `step`
     * deep-links to a wizard step (the GL chip asks for Step 2, Data Sources); without it Calc
     * Builder lands on its usual step for the calculation.
     */
    onOpen: (step?: number) => Promise<void>;
}

/**
 * Header of a revealed CLI column: the CLI (with currency when shown) over its segment → counterparty
 * caption. With `calcBuilder`, the CLI number is a link to that CLI's Calc Builder page (Story 3.5).
 */
export function CliColumnHeader(props: {
    label: string;
    caption?: string;
    cli?: string;
    calcBuilder?: CalcBuilderLinkProps;
    /** Story 3.3 source state for this CLI; omitted when source chips are not shown. */
    sources?: CliSourcesState;
    /** Story 3.4: whether the user's role may open a chip's target module. */
    canOpenModule?: (module: string) => boolean;
}) {
    const { label, caption, cli, calcBuilder, sources, canOpenModule } = props;
    const [opening, setOpening] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const open = (step?: number) => {
        if (!calcBuilder || opening) {
            return;
        }
        setOpening(true);
        setError(undefined);
        calcBuilder
            .onOpen(step)
            .catch((failure) => setError(ErrorUtils.getMessage(failure) || CALC_BUILDER_LOOKUP_FAILED_MESSAGE))
            .finally(() => setOpening(false));
    };

    return (
        <div className="lef-cli-provenance__cli-header">
            {calcBuilder ? (
                <Button
                    variant="inline-link"
                    iconName="external"
                    iconAlign="right"
                    loading={opening}
                    disabled={!calcBuilder.canOpen}
                    disabledReason={calcBuilder.canOpen ? undefined : CALC_BUILDER_NO_ACCESS_MESSAGE}
                    ariaLabel={`Open ${cli ?? label} in Calc Builder (opens in a new tab)`}
                    onClick={() => open()}>
                    {label}
                </Button>
            ) : (
                <span>{label}</span>
            )}
            {caption && <span className="lef-cli-provenance__caption">{caption}</span>}
            {sources && (
                <SourceChips
                    sources={sources}
                    canOpenModule={canOpenModule}
                    // The GL chip opens Calc Builder through this header's own handler, so a failed
                    // lookup shows the same error line as a failed click on the CLI number.
                    calcBuilder={calcBuilder && { canOpen: calcBuilder.canOpen, opening, onOpen: open }}
                />
            )}
            {error && (
                <span className="lef-cli-provenance__caption lef-cli-provenance__caption--error" role="alert">
                    {error}
                </span>
            )}
        </div>
    );
}

export const SOURCE_NO_ACCESS_MESSAGE = 'Your role is not authorized to view this source.';
export const SOURCE_NO_PAGE_MESSAGE = 'This source has no page to open.';
export const SOURCE_CURRENT_VERSION_NOTE = 'Opens the current version, which may differ from the one used for this report.';

/**
 * "Source: GL Balance ↗ MDA ↗ CDT1 ↗" row under a revealed CLI (Story 3.3). Each chip opens its origin
 * in a new tab (Story 3.4); a role without access to the target module gets a disabled chip with the
 * reason instead of a page that would refuse it.
 */
export function SourceChips(props: {
    sources: CliSourcesState;
    canOpenModule?: (module: string) => boolean;
    /**
     * Story 3.5 link of the owning CLI header; GL chips open Calc Builder through it. `onOpen` is the
     * header's own click handler, which owns the busy state and shows any lookup error under the CLI.
     */
    calcBuilder?: { canOpen: boolean; opening: boolean; onOpen: (step?: number) => void };
}) {
    const { sources, canOpenModule, calcBuilder } = props;

    if (sources.status === 'loading') {
        return (
            <div className="lef-cli-source__row">
                <span className="lef-cli-source__label">
                    <Spinner size="normal" /> Loading sources…
                </span>
            </div>
        );
    }
    if (sources.status === 'error') {
        return (
            <div className="lef-cli-source__row">
                <span className="lef-cli-provenance__caption lef-cli-provenance__caption--error" title={sources.message}>
                    Sources could not be loaded: {sources.message}
                </span>
            </div>
        );
    }
    if (sources.status === 'unavailable') {
        return (
            <div className="lef-cli-source__row">
                <span className="lef-cli-source__label" title={sources.reason}>
                    Sources unavailable — {sources.reason}
                </span>
            </div>
        );
    }

    return (
        <div className="lef-cli-source__row">
            <span className="lef-cli-source__label">Source:</span>
            {sources.chips.map((chip) => {
                const allowed = canOpenModule ? canOpenModule(chip.module) : true;
                const viaCalcBuilder = !!chip.opensCalcBuilder;
                const openable = allowed && (viaCalcBuilder ? !!calcBuilder?.canOpen : !!chip.href);
                const busy = viaCalcBuilder && !!calcBuilder?.opening;
                const reason = !allowed ? SOURCE_NO_ACCESS_MESSAGE : !openable ? SOURCE_NO_PAGE_MESSAGE : undefined;
                const hint = [chip.title, reason ?? (chip.versionPinned ? undefined : SOURCE_CURRENT_VERSION_NOTE)]
                    .filter(Boolean)
                    .join(' — ');
                return (
                    <button
                        key={chip.label}
                        type="button"
                        className={`lef-cli-source__chip lef-cli-source__chip--${chip.type}`}
                        title={hint}
                        aria-label={`${chip.label}: ${hint}${openable ? ' (opens in a new tab)' : ''}`}
                        aria-busy={busy || undefined}
                        disabled={!openable || busy}
                        onClick={() => {
                            if (!openable) {
                                return;
                            }
                            if (viaCalcBuilder) {
                                // A GL balance's origin is the CLI's Step 2 (Data Sources), not the step Calc
                                // Builder would otherwise land on (Step 5 for a reviewed calculation).
                                calcBuilder!.onOpen(WIZARD_STEP_NUMBER.DATA_SOURCES);
                            } else {
                                window.open(chip.href as string, '_blank', 'noopener');
                            }
                        }}>
                        {chip.label}
                        {openable && <span aria-hidden="true">↗</span>}
                    </button>
                );
            })}
        </div>
    );
}
